import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _cx from 'classnames/bind'
import { noop } from 'lodash'
import { enums } from '@adc/sdk'
import type {
  Listing,
  OnlineAuctionSegment,
  LiveAuctionSegment,
  Maybe,
  User,
  OnlineAuctionSegmentBidType,
  LiveAuctionSegmentBidType,
} from '@adc/sdk/types'
import { Text, CheckboxField, Alert, RichTextView } from '@adc/ui-core'
import { useApplicationContext } from '../auctionApplication/context'
import cls from './styles.scss'
import MatchBidContent from './contentMatchBid'
import Content from './content'
import ModalAddress from './modalAddress'
import { usePurchaseProfiles } from './usePurchaseProfiles/usePurchaseProfiles'
import PurchaseProfileSelector from './purchaseProfileSelector'
// TODO: POST MVP - BUY-2128
// import PaymentDetails from '../paymentDetails'

const cx = _cx.bind(cls)

const BID_PENDING = 'Placing your bid now...'
const DISCLAIMER =
  "Bids are not reversible. Make sure you've completed your research before bidding."
const BID_AMOUNT_CHANGED =
  'The bid changed while you were confirming, do you want to confirm at the new price?'
// TODO: POST MVP - BUY-2128
// const PRIVATE_SELLER_TERMS_DISCLAIMER =
//   "Read over the seller's terms below. By confirming your bid you are agreeing to the seller's terms. Bids are not reversible. Make sure you've completed your due diligence before bidding."

//   type BidConfirmationOnConfirmArgs =
//   | { selectedPurchaseProfile: ReoPurchaseProfile }
//   | { selectedPurchaseProfile: TrusteePurchaseProfile }

// type BidConfirmationOnConfirm = (args: BidConfirmationOnConfirmArgs) => void

type BidType =
  | OnlineAuctionSegmentBidType.Regular // figure out whether to render FCL variant or not based off listing
  | OnlineAuctionSegmentBidType.MaxBid
  | OnlineAuctionSegmentBidType.Match
  | LiveAuctionSegmentBidType.RemoteBid

// TODO: we're gonna do this, just not right now, refer to OnlineAuctionsData type in blueprint bidding hooks
// type BlueprintBiddingComponentProps<T> = {
//   listing: BlueprintBiddingCollection<Listing>,
//   segment: BlueprintBiddingCollection<T>
// }

interface BidConfirmationContentProps {
  bidAmount: number
  isPerformingBid: boolean
  showAmountChangedMessage?: boolean
  listing: Listing
  segment: OnlineAuctionSegment | LiveAuctionSegment
  user: User // TODO: get this from app context
  type: BidType
  isOccupancyDisclaimerRequired?: boolean // TODO: calculate these from featureFLags + props, featureFlags come from app context
  occupancyDisclaimerAcknowledged?: boolean
  showListingAddress?: boolean
  setOccupancyAcknowledged?: (value: boolean) => void
  handlePurchaseProfileSelection?: (value: Maybe<string>) => void
}
interface CostCalculations {
  buyersPremium: number
  totalPurchasePrice: number
  earnestMoneyDeposit: number
}

interface PurchaseProfileHook {
  defaultPurchaseProfile: Maybe<string>
  dropdownOptions: Array<any>
  isRequired: boolean
  createLink: string
}

function useOnce<T>(set: () => T) {
  const [value] = useState(set)
  return value
}

// TODO: purchase profiles web socket realtime updating
// TODO: detect whether purchase profile selection is enabled/required based off the segment configuration
export const BidConfirmationContent = ({
  bidAmount,
  isPerformingBid,
  listing,
  segment,
  user,
  type,
  isOccupancyDisclaimerRequired = false,
  occupancyDisclaimerAcknowledged = false,
  showListingAddress = false,
  setOccupancyAcknowledged = noop,
  handlePurchaseProfileSelection = noop,
}: BidConfirmationContentProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const calculations: {
    buyersPremium: number
    totalPurchasePrice: number
    earnestMoneyDeposit: number
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  } = (listing as any).__methods__.getCostCalculations(
    bidAmount
  ) as CostCalculations

  const initialBidAmount = useOnce(() => bidAmount)

  const additionalSellerTerms = listing.listing_remarks?.additional_seller_terms

  const listingAddress = listing.formatted_address?.[0]

  // @ts-ignore allow undefined
  const { is_bidding_purchase_profile_required_enabled } =
    segment?.configuration || {}
  const currentUserBidAmount = segment.current_user_highest_bid?.bid_amount
  const { buyersPremium, totalPurchasePrice, earnestMoneyDeposit } =
    calculations
  const { usingSellerLevelFees, isMatchBid } = useMemo(
    () => ({
      usingSellerLevelFees:
        listing.listing_configuration?.is_buyers_premium_available ||
        listing.listing_configuration?.is_earnest_money_deposit_available,
      isMatchBid: type === enums.OnlineAuctionSegmentBidType.Match,
    }),
    [listing, type]
  )

  const { graphWebsocket } = useApplicationContext()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment

  const isTypeRemoteBid = type === enums.LiveAuctionSegmentBidType.RemoteBid
  const isTypeMatchBid = type === enums.OnlineAuctionSegmentBidType.Match

  const { defaultPurchaseProfile, dropdownOptions, isRequired, createLink } =
    usePurchaseProfiles({
      user,
      isREO:
        listing.listing_configuration?.asset_type === enums.AssetType.BankOwned,
      graphWebsocket,
    }) as PurchaseProfileHook

  const selectedPurchaseProfileInSegment = isTypeRemoteBid
    ? (segment as LiveAuctionSegment).current_user_remote_bid
        ?.purchase_profile_id
    : null
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [selectedPurchaseProfile, setSelectedPurchaseProfile] = useState(
    selectedPurchaseProfileInSegment || defaultPurchaseProfile
  )

  useEffect(() => {
    handlePurchaseProfileSelection &&
      handlePurchaseProfileSelection(selectedPurchaseProfile)
  }, [selectedPurchaseProfile])

  const handleOnChangePurchaseProfile = useCallback(
    (_, data) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      setSelectedPurchaseProfile(data?.value)
    },
    [setSelectedPurchaseProfile]
  )

  // TODO: POST MVP - BUY-2128
  // const showPaymentDetails =
  //   listing.listing_configuration?.asset_type ===
  //     enums.AssetType.PrivateSeller &&
  //   get(featureFlags, 'fe.resi.enable-private-seller') &&
  //   segment?.current_user_highest_bid

  return (
    <div>
      {showListingAddress && listingAddress && (
        <ModalAddress address={listingAddress} />
      )}
      {isMatchBid ? (
        <MatchBidContent
          bidAmount={bidAmount}
          buyersPremium={buyersPremium}
          earnestMoneyDeposit={earnestMoneyDeposit}
          currentUserBidAmount={currentUserBidAmount}
          totalPurchasePrice={totalPurchasePrice}
        />
      ) : (
        <Content
          bidAmount={bidAmount}
          buyersPremium={buyersPremium}
          earnestMoneyDeposit={earnestMoneyDeposit}
          usingSellerLevelFees={usingSellerLevelFees}
          totalPurchasePrice={totalPurchasePrice}
          listing={listing}
          segment={segment}
          type={type}
        />
      )}

      {(isTypeRemoteBid ||
        isTypeMatchBid ||
        is_bidding_purchase_profile_required_enabled) && ( // match bid requires this but doesn't set the flag to true, remote bid does use this flag so can be removed from the type conditions
        <PurchaseProfileSelector
          handleOnChangePurchaseProfile={handleOnChangePurchaseProfile}
          defaultPurchaseProfile={defaultPurchaseProfile}
          dropdownOptions={dropdownOptions}
          isRequired={isRequired}
          selectedPurchaseProfile={selectedPurchaseProfile}
          createLink={isTypeRemoteBid ? '' : createLink}
          disabled={!!selectedPurchaseProfileInSegment}
        />
      )}

      {isOccupancyDisclaimerRequired && (
        <Text className='u-mb-2' size='tiny' color='gray'>
          <CheckboxField
            checked={occupancyDisclaimerAcknowledged}
            onChange={() =>
              setOccupancyAcknowledged(!occupancyDisclaimerAcknowledged)
            }
            elmId='bid_confirmation_occupancy_acknowledgement_checkbox'
            label='I acknowledge that this property may be occupied and require action, up to and including eviction, to take full possession after closing.'
          />
        </Text>
      )}
      <div className={cx('disclaimer', 'u-mb-2')} data-elm-id='bid_disclaimer'>
        {DISCLAIMER}
      </div>
      {isPerformingBid && (
        <p className={cx('u-mb-2', 'u-mt-1')}>{BID_PENDING}</p>
      )}
      {bidAmount !== initialBidAmount && (
        <Alert
          header='Bid Amount Changed'
          role='caution'
          showIcon
          content={BID_AMOUNT_CHANGED}
        />
      )}

      {additionalSellerTerms && (
        <>
          {/* TODO: POST MVP - BUY-2128
            {showPaymentDetails ? (
            <div className={cx('u-mb-2')} data-elm-id='payment_details'>
              <div
                className={cx('disclaimer', 'u-mb-2')}
                data-elm-id='private_seller_terms_disclaimer'
              >
                {PRIVATE_SELLER_TERMS_DISCLAIMER}
              </div>
              <PaymentDetails
                bid={
                  segment.current_user_highest_bid as OnlineAuctionSegmentBid
                }
              />
            </div>
          ) : ( */}
          <div
            className={cx('u-mb-2')}
            data-elm-id='additional_seller_terms_disclaimer'
          >
            <Text content='Reminder: Important Sale Terms' />
            <RichTextView
              content={additionalSellerTerms}
              elmId='bid_confirmation_seller_terms'
            />
          </div>
          {/* )} */}
        </>
      )}
    </div>
  )
}
